import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { RecordedMatchSections } from "./RecordedMatchSections";
import { X, MapPin, Calendar, Share2, Trophy, Sparkles, Loader2, Star, Award } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PredictionWidget = ({ fixture, getAuthHeaders }) => {
  const [home, setHome] = useState(0);
  const [away, setAway] = useState(0);
  const [saving, setSaving] = useState(false);
  const [existing, setExisting] = useState(null);
  const [locked, setLocked] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!fixture) return undefined;
    let cancelled = false;
    // Single source of truth: hit /upcoming and find this fixture's prediction.
    axios.get(`${API}/mobile/predictions/upcoming`, { headers: getAuthHeaders() })
      .then(r => {
        if (cancelled) return;
        const row = (r.data || []).find(x => x.fixture_id === fixture.id);
        if (row) {
          setLocked(Boolean(row.locked));
          if (row.my_prediction) {
            setExisting(row.my_prediction);
            setHome(row.my_prediction.home_score || 0);
            setAway(row.my_prediction.away_score || 0);
          }
        }
        setLoaded(true);
      })
      .catch(() => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
  }, [fixture, getAuthHeaders]);

  if (!fixture || (fixture.status && !["Scheduled", "Live"].includes(fixture.status))) return null;
  if (!loaded) return null;

  const submit = async () => {
    if (locked || saving) return;
    setSaving(true);
    try {
      await axios.post(
        `${API}/mobile/predictions`,
        { fixture_id: fixture.id, home_score: Number(home), away_score: Number(away) },
        { headers: getAuthHeaders() }
      );
      setExisting({ home_score: Number(home), away_score: Number(away) });
    } catch (e) {
      alert(e?.response?.data?.detail || "Σφαλμα");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="bg-gradient-to-br from-[#1a1208] via-[#121212] to-[#121212] border border-[#F5A623]/30 rounded-2xl p-4"
      data-testid="match-prediction-widget"
    >
      <div className="flex items-center gap-2 mb-3">
        <Sparkles size={14} className="text-[#F5A623]" />
        <span className="text-[10px] uppercase tracking-[0.25em] text-[#F5A623] font-bold" style={{ paddingLeft: "0.25em" }}>
          {locked ? "Οι προβλεψεις εκλεισαν" : existing ? "Η προβλεψη σου" : "Προβλεψε το σκορ"}
        </span>
      </div>
      <div className="grid grid-cols-3 items-center gap-2">
        <div className="text-center">
          <p className="text-zinc-300 text-xs uppercase tracking-wider truncate mb-1">{fixture.home_team}</p>
          <ScoreStepper value={home} onChange={setHome} disabled={locked} testid="pred-home" />
        </div>
        <div className="text-center">
          <p className="font-['Bebas_Neue'] text-zinc-600 text-2xl">vs</p>
        </div>
        <div className="text-center">
          <p className="text-zinc-300 text-xs uppercase tracking-wider truncate mb-1">{fixture.away_team}</p>
          <ScoreStepper value={away} onChange={setAway} disabled={locked} testid="pred-away" />
        </div>
      </div>
      {!locked && (
        <button
          onClick={submit}
          disabled={saving}
          className="w-full mt-4 bg-[#F5A623] text-black text-sm font-bold uppercase tracking-wider py-2.5 rounded-full hover:bg-[#e09620] transition-colors disabled:opacity-60"
          data-testid="pred-submit"
        >
          {saving ? "..." : existing ? "Ενημερωση" : "Αποστολη Προβλεψης"}
        </button>
      )}
      {existing && (
        <p className="text-center text-zinc-500 text-[10px] mt-2">
          {locked ? "Περιμενουμε το τελικο σκορ" : "Μπορεις να αλλαξεις μεχρι 30 λεπτα πριν τη σεντρα"}
        </p>
      )}
    </div>
  );
};

const ScoreStepper = ({ value, onChange, disabled, testid }) => (
  <div className="flex items-center justify-center gap-2">
    <button
      onClick={() => onChange(Math.max(0, value - 1))}
      disabled={disabled}
      className="w-8 h-8 rounded-full bg-[#1a1a1a] border border-[#262626] text-white text-lg hover:border-[#F5A623]/40 disabled:opacity-40"
      data-testid={`${testid}-minus`}
    >−</button>
    <span className="font-['Bebas_Neue'] text-3xl text-[#F5A623] w-8 text-center leading-none" data-testid={`${testid}-value`}>{value}</span>
    <button
      onClick={() => onChange(Math.min(20, value + 1))}
      disabled={disabled}
      className="w-8 h-8 rounded-full bg-[#1a1a1a] border border-[#262626] text-white text-lg hover:border-[#F5A623]/40 disabled:opacity-40"
      data-testid={`${testid}-plus`}
    >+</button>
  </div>
);

/**
 * MatchDetailPage — bottom-sheet style overlay that takes the full viewport.
 * Opens whenever a fixture is tapped from anywhere (Πρόγραμμα list, Live banner,
 * home feed match card). Contains: teams + score + status + venue + date,
 * "Πρόβλεψε το σκορ" widget for Scheduled/Live matches, share button.
 */
const MatchDetailPage = ({ fixtureId, onClose, getAuthHeaders }) => {
  const [fixture, setFixture] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!fixtureId) return undefined;
    let cancelled = false;
    setLoading(true); setFixture(null); setDetail(null);
    axios.get(`${API}/fixtures/${fixtureId}/detail`)
      .then(r => { if (!cancelled) { setFixture(r.data.fixture); setDetail(r.data); setLoading(false); } })
      .catch(() => { if (!cancelled) { setFixture(null); setLoading(false); } });
    return () => { cancelled = true; };
  }, [fixtureId]);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  const safeGetAuthHeaders = useMemo(() => {
    return getAuthHeaders || (() => {
      const t = typeof window !== "undefined" ? window.localStorage.getItem("mobile_token") : null;
      return t ? { Authorization: `Bearer ${t}` } : {};
    });
  }, [getAuthHeaders]);

  const dateText = useMemo(() => {
    if (!fixture?.match_date) return "";
    try {
      const d = new Date(fixture.match_date);
      const date = d.toLocaleDateString("el-GR", { weekday: "long", day: "2-digit", month: "long" });
      const time = fixture.match_time || (fixture.match_date.includes("T") ? d.toLocaleTimeString("el-GR", { hour: "2-digit", minute: "2-digit" }) : "");
      return `${date}${time ? ` · ${time}` : ""}`;
    } catch (_) { return fixture.match_date; }
  }, [fixture]);

  const isLive = fixture?.status === "Live";
  const isFinished = ["completed", "finished", "ft", "final"].includes((fixture?.status || "").toLowerCase());
  const isAcademy = Boolean(fixture?.academy_group_id || fixture?.team_type === "Academy");

  const shareMatch = async () => {
    if (!fixture) return;
    // Use the OG endpoint so messengers scrape a rich preview card.
    const url = `${window.location.origin}/api/share/match/${fixture.id}`;
    const text = `${fixture.home_team} vs ${fixture.away_team} — Λευτερια FC`;
    try {
      if (navigator.share) await navigator.share({ title: fixture.home_team + " vs " + fixture.away_team, text, url });
      else { await navigator.clipboard.writeText(url); alert("Ο συνδεσμος αντιγραφηκε!"); }
    } catch (_e) { /* user cancelled */ }
  };

  if (!fixtureId) return null;

  return (
    <div
      className="mobile-full-page"
      onClick={onClose}
      data-testid="match-detail-overlay"
    >
      <div
        className="relative bg-[#0a0a0a] w-full max-w-md h-[94vh] rounded-t-2xl border-t border-[#262626] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="match-detail-page"
        style={{ animation: "matchSlide 0.28s cubic-bezier(0.16,1,0.3,1) both" }}
      >
        <div className="mobile-sheet-handle flex justify-center pt-2 pb-1">
          <div className="w-10 h-1 rounded-full bg-zinc-700" />
        </div>
        <button onClick={onClose} aria-label="Κλείσιμο αγώνα" className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-black/60 backdrop-blur flex items-center justify-center text-zinc-300 hover:text-white" data-testid="match-detail-close">
          <X size={18} />
        </button>

        {loading && (
          <div className="flex-1 flex items-center justify-center" data-testid="match-detail-loading">
            <Loader2 size={26} className="text-[#F5A623] animate-spin" />
          </div>
        )}

        {!loading && !fixture && (
          <div role="alert" data-testid="match-detail-error" className="flex-1 flex items-center justify-center px-6 text-center">
            <p className="text-zinc-400 text-sm">Ο αγωνας δεν βρεθηκε.</p>
          </div>
        )}

        {!loading && fixture && (
          <div className="flex-1 overflow-y-auto px-4 pt-3 pb-8 space-y-4">
            {/* Status banner */}
            {isLive && (
              <div className="flex items-center justify-center gap-2 bg-red-600/15 border border-red-600/40 text-red-400 rounded-full py-1.5 text-[11px] uppercase tracking-[0.3em] font-bold" data-testid="match-status-live">
                <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                Ζωντανά
              </div>
            )}
            {isFinished && (
              <div className="flex items-center justify-center bg-zinc-800 text-zinc-300 rounded-full py-1.5 text-[11px] uppercase tracking-[0.3em] font-bold" data-testid="match-status-finished">
                Τελικο
              </div>
            )}

            {/* Header — teams + score */}
            <div className="bg-gradient-to-b from-[#1a1208] to-[#0a0a0a] border border-[#262626] rounded-2xl p-4">
              <p data-testid="match-detail-competition" className="text-center text-xs text-[#F5A623] font-bold mb-3">
                {fixture.competition || ""}
              </p>
              <div className="grid grid-cols-3 items-center gap-2">
                <p data-testid="match-detail-home-team" className="text-white text-sm font-semibold text-center break-words min-w-0">{fixture.home_team}</p>
                <div className="text-center">
                  {(isLive || isFinished) && fixture.home_score != null && fixture.away_score != null ? (
                    <p data-testid="match-detail-score" className="font-['Bebas_Neue'] text-3xl text-white whitespace-nowrap">
                      {fixture.home_score}<span className="text-zinc-600 px-2">-</span>{fixture.away_score}
                    </p>
                  ) : (
                    <p data-testid="match-detail-no-score" className="font-['Bebas_Neue'] text-3xl text-zinc-600">—</p>
                  )}
                </div>
                <p data-testid="match-detail-away-team" className="text-white text-sm font-semibold text-center break-words min-w-0">{fixture.away_team}</p>
              </div>
            </div>

            {/* Meta */}
            <div className="bg-[#111] border border-[#262626] rounded-xl divide-y divide-[#1a1a1a]">
              <div className="flex items-center gap-2 px-4 py-2.5">
                <Calendar size={13} className="text-[#F5A623]" />
                <span data-testid="match-detail-date" className="text-zinc-200 text-sm">{dateText}</span>
              </div>
              {fixture.venue && (
                <div className="flex items-center gap-2 px-4 py-2.5">
                  <MapPin size={13} className="text-[#F5A623]" />
                  <span data-testid="match-detail-venue" className="text-zinc-200 text-sm">{fixture.venue}</span>
                </div>
              )}
              {fixture.team_type && (
                <div className="flex items-center gap-2 px-4 py-2.5">
                  <Trophy size={13} className="text-[#F5A623]" />
                  <span data-testid="match-detail-team-type" className="text-zinc-200 text-sm">{isAcademy ? "Ακαδημία" : "Πρώτη ομάδα"}</span>
                </div>
              )}
            </div>

            {/* Embedded prediction */}
            {!isAcademy && <PredictionWidget fixture={fixture} getAuthHeaders={safeGetAuthHeaders} />}

            <RecordedMatchSections detail={detail} />

            {/* MVP voting — only shows after the match is completed */}
            <MvpWidget fixture={fixture} getAuthHeaders={safeGetAuthHeaders} />

            {/* Share */}
            <button
              onClick={shareMatch}
              className="w-full bg-[#111] border border-[#262626] hover:border-[#F5A623]/40 text-white text-sm font-semibold py-3 rounded-xl flex items-center justify-center gap-2 transition-colors"
              data-testid="match-share"
            >
              <Share2 size={14} />
              Μοιρασου
            </button>
          </div>
        )}
      </div>
      <style>{`@keyframes matchSlide {0%{transform:translateY(40px);opacity:0}100%{transform:translateY(0);opacity:1}}`}</style>
    </div>
  );
};

export default MatchDetailPage;

// ============================================================================
// MvpWidget — Iter 91.12 — post-match MVP voting.
// Renders only when the fixture status is "completed" (or similar) and the
// 48h voting window is still open. Anyone linked to the squad can cast ONE
// vote; recast replaces their previous choice. Winner shown after close.
// ============================================================================
const MvpWidget = ({ fixture, getAuthHeaders }) => {
  const [data, setData] = useState(null);
  const [squad, setSquad] = useState([]);
  const [voting, setVoting] = useState(false);
  const [loading, setLoading] = useState(true);
  const isDone = ["completed", "finished", "final"].includes((fixture?.status || "").toLowerCase());

  useEffect(() => {
    if (!fixture?.id || !isDone) { setLoading(false); return; }
    let cancelled = false;
    const load = async () => {
      try {
        const [mvp, squadResp] = await Promise.all([
          axios.get(`${API}/mobile/fixtures/${fixture.id}/mvp`, { headers: getAuthHeaders() }),
          axios.get(`${API}/mobile/fixtures/${fixture.id}/squad`, { headers: getAuthHeaders() }).catch(() => ({ data: { players: [] } })),
        ]);
        if (cancelled) return;
        setData(mvp.data);
        setSquad(squadResp.data?.players || []);
      } catch (_e) { /* ignore */ }
      finally { if (!cancelled) setLoading(false); }
    };
    load();
    return () => { cancelled = true; };
  }, [fixture?.id, isDone, getAuthHeaders]);

  const castVote = async (pid) => {
    if (voting || !data?.voting_open) return;
    setVoting(true);
    try {
      await axios.post(`${API}/mobile/fixtures/${fixture.id}/mvp`, { player_id: pid }, { headers: getAuthHeaders() });
      const r = await axios.get(`${API}/mobile/fixtures/${fixture.id}/mvp`, { headers: getAuthHeaders() });
      setData(r.data);
    } catch (e) { alert(e.response?.data?.detail || "Σφαλμα ψηφου"); }
    finally { setVoting(false); }
  };

  if (loading || !isDone || !data) return null;

  return (
    <div className="bg-gradient-to-br from-[#F5A623]/10 to-transparent border border-[#F5A623]/30 rounded-xl overflow-hidden" data-testid="mvp-widget">
      <div className="px-4 py-3 flex items-center gap-2 border-b border-[#F5A623]/20">
        <Award size={16} className="text-[#F5A623]" />
        <div className="flex-1">
          <p className="text-white text-sm font-semibold uppercase tracking-wider">MVP Αγωνα</p>
          <p className="text-[10px] text-zinc-400">
            {data.voting_open ? "Ψηφισε τον καλυτερο παικτη" : (data.winner ? "Νικητης" : "Δεν υπηρξαν ψηφοι")}
          </p>
        </div>
        <span className="text-[10px] text-[#F5A623]">{data.total_votes || 0} ψηφοι</span>
      </div>
      {data.voting_open && (
        <div className="p-3 grid grid-cols-2 gap-2 max-h-64 overflow-y-auto">
          {(squad.length ? squad : (data.leaderboard || []).map(l => l.player)).map(p => (
            <button
              key={p.id}
              onClick={() => castVote(p.id)}
              disabled={voting}
              className={`text-left px-2.5 py-2 rounded-lg border transition-all ${data.my_vote_player_id === p.id ? "bg-[#F5A623] border-[#F5A623] text-black" : "bg-[#0d0d0d] border-[#262626] text-white hover:border-[#F5A623]/50"}`}
              data-testid={`mvp-vote-${p.id}`}
            >
              <div className="flex items-center gap-2">
                {data.my_vote_player_id === p.id && <Star size={12} className="text-black flex-shrink-0" />}
                <span className="text-xs font-medium truncate">{p.name}</span>
              </div>
              {p.number != null && <span className="text-[10px] opacity-70">#{p.number}</span>}
            </button>
          ))}
        </div>
      )}
      {!data.voting_open && data.winner && (
        <div className="p-4 flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-[#F5A623] text-black flex items-center justify-center font-['Bebas_Neue'] text-lg">MVP</div>
          <div className="min-w-0 flex-1">
            <p className="text-white font-semibold truncate">{data.winner.player.name}</p>
            <p className="text-[10px] text-zinc-400">{data.winner.votes} ψηφοι · Κλειστη ψηφοφορια</p>
          </div>
        </div>
      )}
      {!data.voting_open && !data.winner && (
        <p className="p-4 text-xs text-zinc-500 text-center">Η ψηφοφορια εληξε χωρις ψηφους.</p>
      )}
    </div>
  );
};
